import { useState, useEffect, useCallback, useMemo } from 'react';

export interface UseColumnManagerOptions {
  /** Prefijo de clave de localStorage — debe ser único por tabla */
  storageKey: string;
  /** Todas las claves de columna disponibles, en el orden por defecto */
  allKeys: string[];
  /** Claves que nunca se pueden ocultar (ej. "acciones") */
  requiredKeys?: string[];
}

export interface UseColumnManagerResult {
  /** Todas las claves (visibles + ocultas), en el orden actual */
  orderedKeys: string[];
  /** Solo las claves visibles, ya en el orden actual — usar esto para armar columnas de tabla y export */
  orderedVisibleKeys: string[];
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  moveColumn: (activeKey: string, overKey: string) => void;
  resetColumns: () => void;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Maneja visibilidad + orden de columnas para una tabla, persistido en localStorage.
 * Reemplaza la lógica de "columnas visibles" duplicada por tabla — ver
 * docs/plan_unificacion_visual.html sección "Selector de columnas".
 */
export function useColumnManager({ storageKey, allKeys, requiredKeys = [] }: UseColumnManagerOptions): UseColumnManagerResult {
  const orderStorageKey = `${storageKey}_column_order`;
  const visibleStorageKey = `${storageKey}_column_visible`;

  const mergeOrder = useCallback((saved: string[] | null): string[] => {
    const base = (saved || []).filter(k => allKeys.includes(k));
    const missing = allKeys.filter(k => !base.includes(k));
    return [...base, ...missing];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeys.join(',')]);

  const [orderedKeys, setOrderedKeys] = useState<string[]>(() =>
    mergeOrder(readJson<string[]>(orderStorageKey))
  );

  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(() => {
    const saved = readJson<string[]>(visibleStorageKey);
    const set = saved ? new Set(saved.filter(k => allKeys.includes(k))) : new Set(allKeys);
    requiredKeys.forEach(k => set.add(k));
    return set;
  });

  // Si cambia el set de columnas disponibles (nueva versión de la pantalla), incorporar las nuevas
  useEffect(() => {
    setOrderedKeys(prev => mergeOrder(prev));
    setVisibleKeys(prev => {
      const next = new Set(prev);
      allKeys.forEach(k => {
        if (!prev.has(k) && requiredKeys.includes(k)) next.add(k);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeys.join(',')]);

  useEffect(() => {
    localStorage.setItem(orderStorageKey, JSON.stringify(orderedKeys));
  }, [orderedKeys, orderStorageKey]);

  useEffect(() => {
    localStorage.setItem(visibleStorageKey, JSON.stringify(Array.from(visibleKeys)));
  }, [visibleKeys, visibleStorageKey]);

  const toggleColumn = useCallback((key: string) => {
    if (requiredKeys.includes(key)) return;
    setVisibleKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredKeys.join(',')]);

  const moveColumn = useCallback((activeKey: string, overKey: string) => {
    setOrderedKeys(prev => {
      const oldIndex = prev.indexOf(activeKey);
      const newIndex = prev.indexOf(overKey);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
      const next = [...prev];
      next.splice(oldIndex, 1);
      next.splice(newIndex, 0, activeKey);
      return next;
    });
  }, []);

  const resetColumns = useCallback(() => {
    setOrderedKeys([...allKeys]);
    setVisibleKeys(new Set(allKeys));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeys.join(',')]);

  const orderedVisibleKeys = useMemo(
    () => orderedKeys.filter(k => visibleKeys.has(k)),
    [orderedKeys, visibleKeys]
  );

  const isVisible = useCallback((key: string) => visibleKeys.has(key), [visibleKeys]);

  return { orderedKeys, orderedVisibleKeys, isVisible, toggleColumn, moveColumn, resetColumns };
}
